import { createFilterOptions } from "@mui/material";
import { useEffect, useState } from "react";
import {
  useAddMembersToGroupMutation,
  useGroupsQuery,
  useSaveGroupMutation,
} from "../../../../App/Queries/GroupQueries";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";
import { ICandidate } from "../../../../App/Typings/Candidate";
import { IGroup, IGroupFilter, IGroupMember } from "../../../../App/Typings/Groups";
import { IAddToGroupState } from "../../../../App/Typings/State/IAddToGroupState";
import { useCurrentUser } from "../../../../App/Queries/UserQueries";
import { useManageCandidateSelectionContext } from "../../../State/ManageCandidateContext";

export function useAddToGroupState(): IAddToGroupState {
  const {
    getters: { selectedCandidates, selectedCount },
    setters: { setAddToGroupOpen },
  } = useManageCandidateSelectionContext();
  const user = useCurrentUser();
  const { data: groups } = useGroupsQuery();
  const addMembersMutation = useAddMembersToGroupMutation();
  const saveGroupMutation = useSaveGroupMutation("");
  const { displayError, displaySuccess, displayWarning } = useNotification();

  const [groupDropdownList, setGroupDropdownList] = useState<IGroupFilter[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<IGroupFilter>({} as IGroupFilter);
  const [selectedGroupData, setSelectedGroupData] = useState<IGroup | undefined>({} as IGroup);

  useEffect(() => {
    if (groups && groups.items.length > 0) {
      setGroupDropdownList(
        groups.items
          .filter((c) => !c.isDynamic)
          .map(
            (group) =>
              ({
                id: group.id,
                name: group.name,
              }) as IGroupFilter,
          ),
      );
    }
  }, [groups]);

  useEffect(() => {
    if (selectedGroup.id && selectedGroup.id !== "") {
      setSelectedGroupData((prevData) => {
        if (prevData?.id === selectedGroup.id) {
          return prevData;
        }
        return groups?.items.find((group) => group.id === selectedGroup.id);
      });
    }
  }, [groups, selectedGroup]);

  const autoCompleteFilter = createFilterOptions<IGroupFilter>();

  const handleCancelAddToGroup = () => {
    setAddToGroupOpen(false);
    setSelectedGroup({} as IGroupFilter);
  };

  /**
   * @description stores currently selected group
   * @param event
   * @param newValue
   */
  const handleGroupSelect = (event: React.SyntheticEvent, newValue: any, reason: string, details: any) => {
    if (reason === "clear") {
      setSelectedGroup({
        id: "",
        name: "",
      });
    } else if (typeof newValue === "string") {
      setSelectedGroup({
        id: "",
        name: newValue,
      });
    } else if (newValue) {
      newValue.name = newValue.name.replace(/^Add "(.*?)"$/gi, "$1");
      setSelectedGroup(details.option);
    }
  };

  const handleGroupSave = async () => {
    if (!selectedGroup.name) {
      displayWarning("Please select a group from the dropdown");
      return;
    } else if (selectedCount === 0) {
      displayWarning("Please select at least one candidate to add to the group");
      return;
    }
    const isNewGroup = !selectedGroupData?.id;
    const groupMembers: IGroupMember[] = [];

    selectedCandidates.forEach((candidate) => {
      const candidateGroupData: Partial<IGroupMember> = {
        addedById: user.id,
        candidateId: candidate.id,
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        title: candidate.title,
      };
      if (!isNewGroup) {
        candidateGroupData.groupId = selectedGroup.id;
      }
      groupMembers.push(candidateGroupData as IGroupMember);
    });

    if (isNewGroup) {
      await saveGroupMutation.mutateAsync(
        {
          name: selectedGroup.name,
          members: groupMembers,
          id: selectedGroup.id === "" ? undefined : selectedGroup.id,
          isDynamic: false,
          rules: JSON.stringify([]),
        },
        {
          onError: () => displayError("There was an error saving group candidates. Please try again"),
          onSuccess: () => {
            setAddToGroupOpen(false);
            displaySuccess("Group candidates saved successfully!");
          },
        },
      );
    } else {
      await addMembersMutation.mutateAsync(
        {
          candidateIds: groupMembers.map((c) => c.candidateId ?? ""),
          groupId: selectedGroup.id,
          selectAll: false,
          excludedIds: [],
        },
        {
          onError: () => displayError("There was an error saving group candidates. Please try again"),
          onSuccess: () => {
            setAddToGroupOpen(false);
            displaySuccess("Group candidates saved successfully!");
          },
        },
      );
    }
  };

  return {
    getters: {
      autoCompleteFilter,
      groupDropdownList,
      selectedGroup,
    },
    handlers: {
      handleCancelAddToGroup,
      handleGroupSave,
      handleGroupSelect,
    },
    setters: {
      setGroupDropdownList,
      setSelectedGroupData,
    },
  };
}
